/**
 * CompositeChart: bars on the count axis and a line on a second percentage axis
 * over the same month axis, drawn on Recharts (the Wave C catalog's composite
 * quality chart). The legend carries token swatches and toggles each series,
 * every mark names itself through the tooltip and its own title, and the bar
 * click-through contract is preserved through Recharts' own click events.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { strings } from "../brand/strings";

import {
  ChartEmpty,
  CHART_MARGIN,
  Legend,
  markClickProps,
  seriesClass,
  seriesColor,
  surfaceClass,
} from "./shared";

/** One month's composite row: the bar count and the optional line percentage. */
export interface CompositeMonth {
  /** The month label as the axis shows it, e.g. 2026-04. */
  readonly month: string;
  /** The bar value, in counts (inspections conducted). */
  readonly bar: number;
  /** The line value, in percent. Null when the model publishes no series. */
  readonly line: number | null;
}

/** The bar's rounded top, the same path the plain BarChart draws. */
function topRoundedRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): string {
  const r = Math.min(radius, width / 2, Math.max(height, 0));
  const end = y + height;
  return [
    `M ${String(x)} ${String(end)}`,
    `L ${String(x)} ${String(y + r)}`,
    `Q ${String(x)} ${String(y)} ${String(x + r)} ${String(y)}`,
    `L ${String(x + width - r)} ${String(y)}`,
    `Q ${String(x + width)} ${String(y)} ${String(x + width)} ${String(y + r)}`,
    `L ${String(x + width)} ${String(end)}`,
    "Z",
  ].join(" ");
}

interface BarShapeProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly index?: number;
}

export function CompositeChart({
  ariaLabel,
  barLabel,
  lineLabel,
  months,
  onSelectMonth,
}: {
  ariaLabel: string;
  /** The bar series' label (what the legend and tooltip call it). */
  barLabel: string;
  /** The line series' label. */
  lineLabel: string;
  months: readonly CompositeMonth[];
  /**
   * Optional click-through (the chart contract): one callback per month bar.
   * Only wired when the bar has a natural filtered register behind it.
   */
  onSelectMonth?: (month: string, index: number) => void;
}): React.JSX.Element {
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const hasLine = months.some((one) => one.line !== null);
  const barTotal = months.reduce((sum, one) => sum + one.bar, 0);
  if (months.length === 0 || barTotal === 0) {
    return <ChartEmpty />;
  }
  const rows = months.map((one) => ({
    month: one.month,
    bar: one.bar,
    line: one.line,
  }));
  // Series 0 is the bar, series 1 the line; the legend toggles either.
  const visible = (series: number): boolean => !hidden.has(series);
  const barShape = (props: BarShapeProps): React.JSX.Element | null => {
    const { x, y, width, height, index } = props;
    if (x === undefined || y === undefined || width === undefined || height === undefined) {
      return null;
    }
    const month = months[index ?? 0];
    const name = month === undefined ? undefined : strings.charts.mark(month.month, month.bar);
    const click =
      month === undefined || onSelectMonth === undefined
        ? undefined
        : () => {
            onSelectMonth(month.month, index ?? 0);
          };
    const marks = markClickProps(click, seriesClass(0));
    return (
      <path
        d={topRoundedRect(x, y, width, height, 6)}
        fill={seriesColor(0)}
        className={marks.className}
        tabIndex={marks.tabIndex}
        aria-label={name}
        role="img"
        onClick={marks.onClick}
        onKeyDown={marks.onKeyDown}
      >
        {name === undefined ? null : <title>{name}</title>}
      </path>
    );
  };

  return (
    <figure className="q-chart" data-testid="composite-chart">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <ComposedChart
            data={rows}
            margin={CHART_MARGIN}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <CartesianGrid stroke="var(--q-rule)" vertical={false} />
            <XAxis
              dataKey="month"
              interval={0}
              tickLine={false}
              axisLine={{ stroke: "var(--q-rule-strong)" }}
              label={{ value: strings.charts.monthAxis, position: "insideBottom", offset: -2 }}
            />
            <YAxis
              yAxisId="count"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number): string => String(Math.round(value))}
              label={{
                value: strings.charts.inspectionsAxis,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            {hasLine && visible(1) && (
              <YAxis
                yAxisId="percent"
                orientation="right"
                domain={[0, 100]}
                tickFormatter={(value: number): string => `${String(Math.round(value))}%`}
                tickLine={false}
                axisLine={false}
                label={{
                  value: strings.charts.passRateAxis,
                  angle: 90,
                  position: "insideRight",
                  style: { textAnchor: "middle" },
                }}
              />
            )}
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: "var(--q-sig-wash)" }}
              content={({ active: hover, payload, label }) =>
                hover && payload.length > 0 ? (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{String(label)}</p>
                    <ul className="q-chart__tooltip-list">
                      {payload.map((entry, index) => (
                        <li key={index} className="q-chart__tooltip-row">
                          <span
                            aria-hidden="true"
                            className="q-chart__tooltip-swatch"
                            style={{ background: entry.color }}
                          />
                          {entry.name}: {String(entry.value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
            />
            {visible(0) && (
              <Bar
                dataKey="bar"
                name={barLabel}
                yAxisId="count"
                shape={barShape}
                isAnimationActive={false}
                onClick={(_datum, index) => {
                  const picked = months[index];
                  if (onSelectMonth !== undefined && picked !== undefined) {
                    onSelectMonth(picked.month, index);
                  }
                }}
              />
            )}
            {hasLine && visible(1) && (
              <Line
                type="linear"
                dataKey="line"
                name={lineLabel}
                yAxisId="percent"
                stroke={seriesColor(1)}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Legend
        entries={[
          { label: barLabel, index: 0, active: visible(0) },
          { label: lineLabel, index: 1, active: hasLine && visible(1) },
        ]}
        onToggle={(index) => {
          setHidden((previous) => {
            const next = new Set(previous);
            if (next.has(index)) {
              next.delete(index);
            } else {
              next.add(index);
            }
            return next;
          });
        }}
      />
    </figure>
  );
}
